import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BACKGROUNDS } from "@/lib/virtualBackground";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = "/api/socket.io";
const ICE = {
  iceServers: [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
  ],
};

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

export function CallProvider({ children }) {
  const { user } = useAuth();

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidates = useRef([]);
  const remoteSet = useRef(false);
  const peerRef = useRef(null);
  const incomingRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const acRef = useRef(null);
  const vbgRef = useRef(null);
  const iceRef = useRef(ICE);
  const connectTimerRef = useRef(null);
  const callUserRef = useRef(null);
  const callStartRef = useRef(0);
  const reportedRef = useRef(false);
  const mediaRef = useRef("video");
  const roleRef = useRef("caller");
  const callIdRef = useRef("");
  const diagRef = useRef(null);
  const [callDiag, setCallDiag] = useState(null);
  // Mid-call reconnection (ICE restart) so a brief mobile-network drop doesn't kill the audio.
  const reconnectTimerRef = useRef(null);
  const giveUpTimerRef = useRef(null);
  const restartCountRef = useRef(0);
  const negotiatingRef = useRef(false);
  const everConnectedRef = useRef(false);
  const endCallRef = useRef(null);
  const [reconnecting, setReconnecting] = useState(false);
  // Ring-delivery detection (a callee can look "online" from stale presence but their socket is dead).
  const ringDeliveredRef = useRef(false);
  const ringWatchdogRef = useRef(null);
  // Live call-quality meter (packet loss / round-trip) polled from getStats while connected.
  const qualityTimerRef = useRef(null);
  const [callQuality, setCallQuality] = useState(null);
  // In-call text chat (for when audio drops on a weak network).
  const [chatMessages, setChatMessages] = useState([]);
  const [peerTyping, setPeerTyping] = useState(false);
  const typingClearRef = useRef(null);

  // Post a one-line diagnostic of how a real call actually connected (or failed) so
  // cross-network mobile issues are visible server-side (candidate types / relay used / errors).
  const reportCall = useCallback(async (outcome, iceError = "") => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    const pc = pcRef.current;
    const token = localStorage.getItem("pula_token");
    const dg = diagRef.current || {};
    const gathered = Array.from(dg.types || []);
    const body = {
      peer_id: peerRef.current?.user_id || "", role: roleRef.current,
      media: mediaRef.current, outcome, call_id: callIdRef.current || "",
      connection_state: pc?.connectionState || "", ice_connection_state: pc?.iceConnectionState || "",
      ice_error: iceError, duration_ms: callStartRef.current ? Date.now() - callStartRef.current : 0,
      local_candidate_type: "", remote_candidate_type: "", relay_used: false,
      gathered_types: gathered.join(",") || "none", relay_found: gathered.includes("relay"),
      ice_error_count: dg.errCount || 0,
    };
    try {
      if (pc) {
        const stats = await pc.getStats();
        let pair = null;
        const pairs = [];
        stats.forEach((r) => {
          if (r.type === "candidate-pair") {
            if ((r.selected || r.nominated) && r.state === "succeeded") pair = r;
            const l = stats.get(r.localCandidateId); const rm = stats.get(r.remoteCandidateId);
            pairs.push(`${l?.candidateType || "?"}>${rm?.candidateType || "?"}:${r.state}`);
          }
        });
        if (pair) {
          const l = stats.get(pair.localCandidateId); const rm = stats.get(pair.remoteCandidateId);
          body.local_candidate_type = l?.candidateType || "";
          body.remote_candidate_type = rm?.candidateType || "";
          body.relay_used = l?.candidateType === "relay" || rm?.candidateType === "relay";
        }
        // Full pair summary (states of every pair tried) — conclusive for diagnosing relay failure.
        body.candidate_pairs = pairs.slice(0, 12).join(" | ").slice(0, 300);
      }
    } catch { /* ignore */ }
    try {
      await fetch(`${BACKEND_URL}/api/webrtc/call-report`, {
        method: "POST", headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body), keepalive: true,
      });
    } catch { /* ignore */ }
  }, []);

  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | calling | incoming | connected
  const [peer, setPeer] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [media, setMedia] = useState("video");
  const [sharing, setSharing] = useState(false);
  const [onlineIds, setOnlineIds] = useState([]);
  const [wakeup, setWakeup] = useState(null); // {user_id, name, media} — a call placed while you were offline
  const [recording, setRecording] = useState(false);
  const [bgMode, setBgMode] = useState("none");

  // ---- Group call (mesh, up to 4) — parallel to the 1:1 path, its own peer map ----
  const gPeersRef = useRef({});      // remoteUid -> RTCPeerConnection
  const gLocalRef = useRef(null);    // my local MediaStream for the group call
  const gRoomRef = useRef(null);
  const gMediaRef = useRef("video");
  const myUidRef = useRef(null);
  const [groupState, setGroupState] = useState(null);       // null | "incoming" | "active"
  const [groupIncoming, setGroupIncoming] = useState(null); // {room_id, from_name, media}
  const [groupParticipants, setGroupParticipants] = useState([]); // [{user_id,name}]
  const [groupStreams, setGroupStreams] = useState({});     // remoteUid -> MediaStream
  const [groupLocalStream, setGroupLocalStream] = useState(null);
  const [groupMuted, setGroupMuted] = useState(false);
  const [groupCameraOff, setGroupCameraOff] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [groupChatMessages, setGroupChatMessages] = useState([]);
  const [groupTypingName, setGroupTypingName] = useState("");
  const groupTypingClearRef = useRef(null);

  const addParticipant = useCallback((uid, name) => {
    setGroupParticipants((prev) => prev.some((p) => p.user_id === uid) ? prev : [...prev, { user_id: uid, name: name || "Member" }]);
  }, []);

  const removeGroupPeer = useCallback((uid) => {
    const pc = gPeersRef.current[uid];
    if (pc) { try { if (pc._reconnectTimer) clearTimeout(pc._reconnectTimer); if (pc._giveupTimer) clearTimeout(pc._giveupTimer); pc.close(); } catch { /* ignore */ } }
    delete gPeersRef.current[uid];
    setGroupStreams((prev) => { const n = { ...prev }; delete n[uid]; return n; });
    setGroupParticipants((prev) => prev.filter((p) => p.user_id !== uid));
  }, []);

  const makeGroupPeer = useCallback(async (remoteId, isOfferer) => {
    if (gPeersRef.current[remoteId]) return gPeersRef.current[remoteId];
    const pc = new RTCPeerConnection(iceRef.current || ICE);
    pc._pending = [];
    pc._everConnected = false;
    gPeersRef.current[remoteId] = pc;
    gLocalRef.current?.getTracks().forEach((t) => pc.addTrack(t, gLocalRef.current));
    pc.onicecandidate = (e) => { if (e.candidate) socketRef.current?.emit("group_signal", { room_id: gRoomRef.current, to: remoteId, kind: "ice", payload: e.candidate }); };
    pc.ontrack = (e) => { const st = e.streams[0]; setGroupStreams((prev) => ({ ...prev, [remoteId]: st })); };

    // Per-peer auto-reconnect: a brief mobile drop must NOT kick someone out of the group.
    // The pair's offerer (smaller user_id) re-negotiates with an ICE restart; the other side just
    // applies the new offer. We only give up (remove the tile) after ~30s of no recovery.
    const iAmOfferer = () => !!(myUidRef.current && myUidRef.current < remoteId);
    const clearGTimers = () => {
      if (pc._reconnectTimer) { clearTimeout(pc._reconnectTimer); pc._reconnectTimer = null; }
      if (pc._giveupTimer) { clearTimeout(pc._giveupTimer); pc._giveupTimer = null; }
    };
    const restartPeer = async () => {
      if (!iAmOfferer() || gPeersRef.current[remoteId] !== pc) return;
      try {
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        socketRef.current?.emit("group_signal", { room_id: gRoomRef.current, to: remoteId, kind: "offer", payload: offer });
      } catch { /* ignore */ }
    };
    const armGroupReconnect = (immediate) => {
      if (!pc._everConnected) return; // still on the first negotiation — leave that to the join flow
      if (immediate) restartPeer();
      else if (!pc._reconnectTimer) pc._reconnectTimer = setTimeout(() => { pc._reconnectTimer = null; restartPeer(); }, 2500);
      if (!pc._giveupTimer) pc._giveupTimer = setTimeout(() => {
        pc._giveupTimer = null;
        if (gPeersRef.current[remoteId] === pc && pc.connectionState !== "connected") removeGroupPeer(remoteId);
      }, 30000);
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") { pc._everConnected = true; clearGTimers(); }
      else if (st === "disconnected") armGroupReconnect(false);
      else if (st === "failed") { if (pc._everConnected) armGroupReconnect(true); else removeGroupPeer(remoteId); }
      else if (st === "closed") removeGroupPeer(remoteId);
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === "connected" || st === "completed") clearGTimers();
      else if (st === "failed" && pc._everConnected) armGroupReconnect(true);
      else if (st === "disconnected" && pc._everConnected) armGroupReconnect(false);
    };
    if (isOfferer) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current?.emit("group_signal", { room_id: gRoomRef.current, to: remoteId, kind: "offer", payload: offer });
    }
    return pc;
  }, [removeGroupPeer]);

  const ensureGroupLocal = useCallback(async (mode) => {
    if (gLocalRef.current) return gLocalRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: mode === "video" ? { width: { ideal: 640 }, height: { ideal: 480 } } : false });
    gLocalRef.current = stream;
    setGroupLocalStream(stream);
    return stream;
  }, []);

  const teardownGroup = useCallback(() => {
    Object.values(gPeersRef.current).forEach((pc) => { try { pc.close(); } catch { /* ignore */ } });
    gPeersRef.current = {};
    gLocalRef.current?.getTracks().forEach((t) => t.stop());
    gLocalRef.current = null;
    gRoomRef.current = null;
    setGroupStreams({});
    setGroupParticipants([]);
    setGroupLocalStream(null);
    setGroupState(null);
    setGroupIncoming(null);
    setGroupMuted(false);
    setGroupCameraOff(false);
    setGroupTitle("");
    setGroupChatMessages([]);
    setGroupTypingName("");
    if (groupTypingClearRef.current) { clearTimeout(groupTypingClearRef.current); groupTypingClearRef.current = null; }
  }, []);

  const leaveGroup = useCallback(() => {
    if (gRoomRef.current) socketRef.current?.emit("group_leave", { room_id: gRoomRef.current });
    teardownGroup();
  }, [teardownGroup]);

  const joinGroupRoom = useCallback(async (roomId, mode, title = "") => {
    await ensureIce();
    await ensureGroupLocal(mode);
    gRoomRef.current = roomId;
    gMediaRef.current = mode;
    setGroupTitle(title || "");
    setGroupState("active");
    socketRef.current?.emit("group_join", { room_id: roomId, media: mode, title }, async (ack) => {
      if (!ack?.ok) { toast.error(ack?.error === "full" ? "That group call is full." : "Couldn't join the group call."); teardownGroup(); return; }
      if (ack.title) setGroupTitle(ack.title);
      // Deterministic offerer rule: the smaller user_id initiates the offer for each pair.
      for (const p of (ack.participants || [])) {
        addParticipant(p.user_id, p.name);
        if (myUidRef.current && myUidRef.current < p.user_id) makeGroupPeer(p.user_id, true);
      }
    });
  }, [ensureGroupLocal, addParticipant, makeGroupPeer, teardownGroup]);

  const startGroupCall = useCallback(async (invitees = [], mode = "video", title = "") => {
    if (pcRef.current || groupState) { toast.error("You're already in a call."); return; }
    const roomId = `grp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    await joinGroupRoom(roomId, mode, title);
    for (const u of invitees) socketRef.current?.emit("group_invite", { room_id: roomId, to: u.user_id, media: mode });
    if (invitees.length) toast.success(`Group call started — ringing ${invitees.length} ${invitees.length === 1 ? "person" : "people"}.`);
  }, [groupState, joinGroupRoom]);

  const inviteToGroup = useCallback((u) => {
    if (!gRoomRef.current) return;
    if (groupParticipants.length + 1 >= 4) { toast.error("Group calls hold up to 4 people."); return; }
    socketRef.current?.emit("group_invite", { room_id: gRoomRef.current, to: u.user_id, media: gMediaRef.current }, (ack) => {
      if (ack?.ok) toast.success(`Ringing ${u.name}${ack.online ? "" : " — they'll see it when they open Pula"}.`);
      else toast.error(ack?.error === "full" ? "The call is full." : "Couldn't add them.");
    });
  }, [groupParticipants]);

  const acceptGroup = useCallback(async () => {
    const inc = groupIncoming;
    if (!inc) return;
    setGroupIncoming(null);
    await joinGroupRoom(inc.room_id, inc.media || "video", inc.title || "");
  }, [groupIncoming, joinGroupRoom]);

  const declineGroup = useCallback(() => { setGroupIncoming(null); }, []);

  const sendGroupChat = useCallback((text) => {
    const t = String(text || "").trim();
    if (!t || !gRoomRef.current) return;
    socketRef.current?.emit("group_chat", { room_id: gRoomRef.current, text: t });
    setGroupChatMessages((prev) => [...prev, { mine: true, from_name: "You", text: t, ts: Date.now() }]);
  }, []);

  const sendGroupTyping = useCallback((typing) => {
    if (!gRoomRef.current) return;
    socketRef.current?.emit("group_typing", { room_id: gRoomRef.current, typing: !!typing });
  }, []);

  const toggleGroupMute = useCallback(() => {
    const on = !groupMuted; setGroupMuted(on);
    gLocalRef.current?.getAudioTracks().forEach((t) => { t.enabled = !on; });
  }, [groupMuted]);
  const toggleGroupCamera = useCallback(() => {
    const on = !groupCameraOff; setGroupCameraOff(on);
    gLocalRef.current?.getVideoTracks().forEach((t) => { t.enabled = !on; });
  }, [groupCameraOff]);

  useEffect(() => { myUidRef.current = user?.user_id || null; }, [user]);

  // Group-call signalling handlers (attached once the socket is connected).
  useEffect(() => {
    const s = socketRef.current;
    if (!s || !connected) return undefined;
    const onIncoming = (d) => {
      if (pcRef.current || gRoomRef.current) return; // already in a call — ignore quietly
      setGroupIncoming(d);
      setGroupState("incoming");
    };
    const onJoined = (d) => {
      if (d.room_id !== gRoomRef.current) return;
      addParticipant(d.user_id, d.name);
      if (myUidRef.current && myUidRef.current < d.user_id) makeGroupPeer(d.user_id, true);
    };
    const onSignal = async (d) => {
      if (d.room_id !== gRoomRef.current) return;
      const pc = gPeersRef.current[d.from] || await makeGroupPeer(d.from, false);
      if (d.kind === "offer") {
        addParticipant(d.from);
        await pc.setRemoteDescription(new RTCSessionDescription(d.payload));
        for (const c of (pc._pending || [])) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ } }
        pc._pending = [];
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        s.emit("group_signal", { room_id: gRoomRef.current, to: d.from, kind: "answer", payload: ans });
      } else if (d.kind === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(d.payload));
        for (const c of (pc._pending || [])) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ } }
        pc._pending = [];
      } else if (d.kind === "ice") {
        if (pc.remoteDescription && pc.remoteDescription.type) { try { await pc.addIceCandidate(new RTCIceCandidate(d.payload)); } catch { /* ignore */ } }
        else { pc._pending = pc._pending || []; pc._pending.push(d.payload); }
      }
    };
    const onLeft = (d) => { if (d.room_id === gRoomRef.current) removeGroupPeer(d.user_id); };
    const onGroupChat = (d) => {
      if (d.room_id !== gRoomRef.current || !d.text) return;
      setGroupTypingName((n) => (n === d.from_name ? "" : n));
      setGroupChatMessages((prev) => [...prev, { mine: false, from_name: d.from_name || "Member", text: String(d.text), ts: Date.now() }]);
    };
    const onGroupTyping = (d) => {
      if (d.room_id !== gRoomRef.current) return;
      setGroupTypingName(d.typing ? (d.from_name || "Someone") : "");
      if (groupTypingClearRef.current) clearTimeout(groupTypingClearRef.current);
      if (d.typing) groupTypingClearRef.current = setTimeout(() => setGroupTypingName(""), 4000);
    };
    s.on("group_incoming", onIncoming);
    s.on("group_peer_joined", onJoined);
    s.on("group_signal", onSignal);
    s.on("group_peer_left", onLeft);
    s.on("group_chat", onGroupChat);
    s.on("group_typing", onGroupTyping);
    return () => { s.off("group_incoming", onIncoming); s.off("group_peer_joined", onJoined); s.off("group_signal", onSignal); s.off("group_peer_left", onLeft); s.off("group_chat", onGroupChat); s.off("group_typing", onGroupTyping); };
  }, [connected, addParticipant, makeGroupPeer, removeGroupPeer]);

  const cleanup = useCallback(() => {
    if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (giveUpTimerRef.current) { clearTimeout(giveUpTimerRef.current); giveUpTimerRef.current = null; }
    if (ringWatchdogRef.current) { clearTimeout(ringWatchdogRef.current); ringWatchdogRef.current = null; }
    if (qualityTimerRef.current) { clearInterval(qualityTimerRef.current); qualityTimerRef.current = null; }
    ringDeliveredRef.current = false;
    setCallQuality(null);
    setChatMessages([]);
    setPeerTyping(false);
    if (typingClearRef.current) { clearTimeout(typingClearRef.current); typingClearRef.current = null; }
    restartCountRef.current = 0;
    negotiatingRef.current = false;
    everConnectedRef.current = false;
    setReconnecting(false);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
    try { vbgRef.current?.stop(); } catch { /* ignore */ }
    vbgRef.current = null;
    try { pcRef.current?.close(); } catch { /* ignore */ }
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    pendingCandidates.current = [];
    remoteSet.current = false;
    peerRef.current = null;
    incomingRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setPeer(null);
    setIncoming(null);
    setStatus("idle");
    setMuted(false);
    setCameraOff(false);
    setSharing(false);
    setRecording(false);
    setBgMode("none");
    setMedia("video");
    setCallDiag(null);
  }, []);

  // Record the call to the user's own device only (nothing is sent to any server).
  const startRecording = useCallback(() => {
    if (recorderRef.current || !remoteStreamRef.current) return;
    try {
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ac.createMediaStreamDestination();
      [localStreamRef.current, remoteStreamRef.current].forEach((s) => {
        const at = s?.getAudioTracks?.()[0];
        if (at) ac.createMediaStreamSource(new MediaStream([at])).connect(dest);
      });
      acRef.current = ac;
      const tracks = [...dest.stream.getAudioTracks()];
      const rv = remoteStreamRef.current.getVideoTracks?.()[0];
      if (rv) tracks.push(rv);
      const mixed = new MediaStream(tracks);
      const types = ["video/webm;codecs=vp8,opus", "video/webm", "audio/webm"];
      const mimeType = types.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(mixed, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        try {
          const blob = new Blob(chunksRef.current, { type: mr.mimeType || "video/webm" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `pula-call-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.webm`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 8000);
          toast.success("Recording saved to your device.");
        } catch { /* ignore */ }
        try { acRef.current?.close(); } catch { /* ignore */ }
        acRef.current = null;
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      toast.info("Recording started — saved only to your device.");
    } catch {
      toast.error("Recording isn't supported on this device.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (recorderRef.current) stopRecording(); else startRecording();
  }, [startRecording, stopRecording]);

  const flushCandidates = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    for (const c of pendingCandidates.current) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ }
    }
    pendingCandidates.current = [];
  }, []);

  // Guarantee we have TURN/STUN servers before a call. Without TURN a mobile
  // call silently degrades to STUN-only and never connects on carrier NAT.
  const ensureIce = useCallback(async () => {
    const token = localStorage.getItem("pula_token");
    try {
      const r = await fetch(`${BACKEND_URL}/api/webrtc/ice-servers`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      const d = await r.json();
      if (d?.iceServers?.length) iceRef.current = { iceServers: d.iceServers };
    } catch { /* keep last-known/default servers */ }
  }, []);

  const createPeer = useCallback((remoteId) => {
    const cfg = { ...(iceRef.current || ICE) };
    // Default to 'all' (direct + relay) so same-network calls connect instantly and there is always
    // a fallback. Cross-continent mobile calls rely on the (Cloudflare) TURN relay, which 'all' still
    // gathers and tries. Opt-in relay-only for manual testing: localStorage pula_force_relay=1.
    try { if (localStorage.getItem("pula_force_relay") === "1") cfg.iceTransportPolicy = "relay"; } catch { /* ignore */ }
    const pc = new RTCPeerConnection(cfg);
    const iceErrRef = { txt: "" };
    // Live diagnostic so a failed call tells us WHY (did we get a relay path? is TURN reachable?).
    const hasTurn = (cfg.iceServers || []).some((s) => String(s.urls).includes("turn"));
    diagRef.current = { turn: hasTurn, types: new Set(), errCount: 0, lastErr: "" };
    setCallDiag(null);
    const showDiag = (reason) => {
      const d = diagRef.current || {};
      setCallDiag({
        reason,
        turnConfigured: !!d.turn,
        relayFound: (d.types || new Set()).has("relay"),
        candidateTypes: Array.from(d.types || []).join(", ") || "none",
        iceState: pc.iceConnectionState,
        connState: pc.connectionState,
        iceErrors: d.errCount || 0,
        lastErr: d.lastErr || "",
      });
    };
    diagRef.current.showDiag = showDiag;
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const m = /typ (\w+)/.exec(e.candidate.candidate || "");
        if (m) diagRef.current?.types?.add(m[1]);
        socketRef.current?.emit("ice_candidate", { to: remoteId, candidate: e.candidate, call_id: callIdRef.current });
      }
    };
    pc.onicecandidateerror = (e) => {
      if (diagRef.current) { diagRef.current.errCount = (diagRef.current.errCount || 0) + 1; diagRef.current.lastErr = `${e.errorCode} ${e.errorText || ""}`.slice(0, 80); }
      iceErrRef.txt = `err ${e.errorCode} ${e.errorText || ""} @${e.url || ""}`.slice(0, 200);
      console.warn("[call] ICE candidate error:", e.errorCode, e.errorText, e.url);
    };
    pc.ontrack = (e) => { remoteStreamRef.current = e.streams[0]; setRemoteStream(e.streams[0]); };

    const clearReconnectTimers = () => {
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      if (giveUpTimerRef.current) { clearTimeout(giveUpTimerRef.current); giveUpTimerRef.current = null; }
    };
    // Re-establish the media path after a mobile-network blip. Only the caller (offerer) creates the
    // ICE-restart offer to avoid glare; the callee applies it and answers. Sent over a dedicated
    // signalling event so it never re-rings the call.
    const doIceRestart = async () => {
      const p = pcRef.current;
      if (!p || negotiatingRef.current || roleRef.current !== "caller") return;
      if (restartCountRef.current >= 8) return;
      negotiatingRef.current = true;
      restartCountRef.current += 1;
      try {
        const offer = await p.createOffer({ iceRestart: true });
        await p.setLocalDescription(offer);
        socketRef.current?.emit("call_renegotiate", { to: peerRef.current?.user_id, kind: "offer", sdp: offer, call_id: callIdRef.current });
      } catch { /* ignore */ } finally { negotiatingRef.current = false; }
    };
    const armReconnect = (immediate) => {
      setReconnecting(true);
      if (immediate) doIceRestart();
      else if (!reconnectTimerRef.current) {
        reconnectTimerRef.current = setTimeout(() => { reconnectTimerRef.current = null; doIceRestart(); }, 2500);
      }
      if (!giveUpTimerRef.current) {
        giveUpTimerRef.current = setTimeout(() => {
          giveUpTimerRef.current = null;
          if (pcRef.current && pcRef.current.connectionState !== "connected") {
            toast.error("Lost the connection — the call ended.");
            endCallRef.current?.();
          }
        }, 30000);
      }
    };

    // Live signal-strength meter: poll audio packet-loss + round-trip so the UI can show a
    // green/amber/red dot and the member can see when the network (not the app) is weak.
    const startQualityMonitor = () => {
      if (qualityTimerRef.current) return;
      setCallQuality({ level: "good", loss: 0, rtt: 0 }); // show a fresh-call dot instantly
      let lastLost = 0, lastRecv = 0, primed = false;
      qualityTimerRef.current = setInterval(async () => {
        const p = pcRef.current;
        if (!p) return;
        try {
          const stats = await p.getStats();
          let inbound = null, pair = null;
          stats.forEach((r) => {
            if (r.type === "inbound-rtp" && r.kind === "audio") inbound = r;
            if (r.type === "candidate-pair" && (r.selected || r.nominated) && r.state === "succeeded") pair = r;
          });
          let loss = 0;
          if (inbound) {
            const dl = (inbound.packetsLost || 0) - lastLost;
            const dr = (inbound.packetsReceived || 0) - lastRecv;
            if (primed && dr + dl > 0) loss = Math.max(0, dl) / (dr + dl);
            lastLost = inbound.packetsLost || 0;
            lastRecv = inbound.packetsReceived || 0;
            primed = true;
          }
          const rtt = pair?.currentRoundTripTime != null ? pair.currentRoundTripTime : 0;
          let level = "good";
          if (loss > 0.08 || rtt > 0.6) level = "weak";
          else if (loss > 0.03 || rtt > 0.35) level = "fair";
          setCallQuality({ level, loss, rtt });
        } catch { /* ignore */ }
      }, 2500);
    };

    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      console.log("[call] connectionState:", st);
      if (st === "connected") {
        everConnectedRef.current = true;
        setStatus("connected");
        setReconnecting(false);
        setCallDiag(null);
        restartCountRef.current = 0;
        clearReconnectTimers();
        if (ringWatchdogRef.current) { clearTimeout(ringWatchdogRef.current); ringWatchdogRef.current = null; }
        if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
        startQualityMonitor();
        reportCall("connected");
      } else if (st === "disconnected") {
        if (everConnectedRef.current) armReconnect(false);
      } else if (st === "failed") {
        if (everConnectedRef.current) armReconnect(true);
        else { showDiag("connection failed"); reportCall("failed", iceErrRef.txt || "connectionState=failed"); }
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      console.log("[call] iceConnectionState:", st);
      if (st === "connected" || st === "completed") {
        setReconnecting(false);
        clearReconnectTimers();
      } else if (st === "failed") {
        if (everConnectedRef.current) armReconnect(true);
        else { showDiag("ICE failed"); reportCall("ice_failed", iceErrRef.txt || "iceConnectionState=failed"); }
      } else if (st === "disconnected") {
        if (everConnectedRef.current) armReconnect(false);
      }
    };
    // Watchdog: if media doesn't connect, surface the diagnostic (don't just silently drop).
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    connectTimerRef.current = setTimeout(() => {
      if (pcRef.current && pcRef.current.connectionState !== "connected") {
        showDiag("timed out");
        reportCall("timeout", iceErrRef.txt || "watchdog_45s");
      }
    }, 45000);
    pcRef.current = pc;
    return pc;
  }, [cleanup, reportCall]);

  const getLocal = useCallback(async (pc, mode) => {
    const constraints = {
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: mode === "video" ? { width: { ideal: 640 }, height: { ideal: 480 } } : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    setLocalStream(stream);
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    return stream;
  }, []);

  // Connect the signalling socket whenever the member is logged in.
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem("pula_token");
    if (!token) return undefined;
    // Load TURN/STUN ICE servers from the backend (TURN relay lets calls connect on mobile networks).
    ensureIce();
    // WebSocket transport (tested reliable on this ingress; the caller's live call_ping proves
    // WS connects on production). Presence + signalling are MongoDB-shared and multi-instance safe.
    // Auto-reconnect keeps presence fresh so a brief network blip doesn't drop "online".
    const s = io(BACKEND_URL, {
      path: SOCKET_PATH, auth: { token },
      transports: ["websocket"], upgrade: false,
      reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000,
      reconnectionDelayMax: 5000, timeout: 20000,
    });
    socketRef.current = s;

    s.on("connect", () => {
      setConnected(true);
      // Reliable, client-initiated presence fetch (the server's connect-time
      // snapshot can race on the polling transport).
      s.emit("presence_sync", {}, (res) => setOnlineIds(res?.online || []));
    });
    s.on("disconnect", () => { setConnected(false); setOnlineIds([]); });

    s.on("presence_snapshot", (d) => setOnlineIds(d?.online || []));
    s.on("presence_update", (d) => setOnlineIds((prev) => {
      const set = new Set(prev);
      if (d?.online) set.add(d.user_id); else set.delete(d.user_id);
      return [...set];
    }));

    s.on("call_incoming", (d) => {
      if (pcRef.current || incomingRef.current) { s.emit("call_reject", { to: d.from }); return; }
      // Confirm to the caller that the ring actually reached us (distinguishes a live socket
      // from stale "online" presence where the ring never arrives).
      s.emit("call_ring_ack", { to: d.from, call_id: d.call_id || "" });
      incomingRef.current = d;
      callIdRef.current = d.call_id || "";
      peerRef.current = { user_id: d.from, name: d.from_name, pic: d.from_pic };
      setIncoming(d);
      setPeer({ user_id: d.from, name: d.from_name, pic: d.from_pic });
      setMedia(d.media || "video");
      setStatus("incoming");
    });

    s.on("call_answered", async (d) => {
      try {
        await pcRef.current?.setRemoteDescription(new RTCSessionDescription(d.answer));
        remoteSet.current = true;
        await flushCandidates();
        // Do NOT mark connected here — wait for the real media connection (onconnectionstatechange).
      } catch (e) { console.error("answer error", e); }
    });

    // Mid-call ICE-restart handshake (re-establishes a dropped media path without re-ringing).
    s.on("call_renegotiate", async (d) => {
      const pc = pcRef.current;
      if (!pc || !d?.sdp) return;
      // Defence-in-depth: only accept renegotiation from the peer of the active call.
      if (d.from && peerRef.current?.user_id && d.from !== peerRef.current.user_id) return;
      try {
        if (d.kind === "offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
          remoteSet.current = true;
          await flushCandidates();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socketRef.current?.emit("call_renegotiate", { to: peerRef.current?.user_id, kind: "answer", sdp: answer, call_id: callIdRef.current });
        } else if (d.kind === "answer") {
          await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
          remoteSet.current = true;
          await flushCandidates();
        }
      } catch (e) { console.error("renegotiate error", e); }
    });

    // Someone called you while your app was closed — surfaced when you open Pula so you can answer.
    s.on("incoming_call_waiting", (d) => {
      if (pcRef.current || incomingRef.current) return;
      setWakeup({ user_id: d.from, name: d.from_name || "Someone", pic: d.from_pic, media: d.media || "video" });
    });

    s.on("ice_candidate", async (d) => {
      if (!d?.candidate) return;
      // Buffer until BOTH the peer connection exists AND the remote description is set.
      // Critical fix: on the callee, the caller's candidates (incl. the relay path) trickle in
      // during "ringing" — before acceptCall() has created the PC. Dropping them there loses the
      // caller's relay candidate, so cross-network calls (which REQUIRE relay) never connect.
      // Queue them instead; flushCandidates() applies them right after setRemoteDescription().
      if (pcRef.current && remoteSet.current) {
        try { await pcRef.current.addIceCandidate(new RTCIceCandidate(d.candidate)); } catch { /* ignore */ }
      } else {
        pendingCandidates.current.push(d.candidate);
      }
    });

    s.on("call_rejected", () => { toast.info(`${peerRef.current?.name || "They"} can't take the call right now.`); cleanup(); });
    s.on("call_ended", () => { toast.info("Call ended."); cleanup(); });

    // The callee's device confirmed the ring landed — cancel the "not reachable" watchdog.
    s.on("call_ring_delivered", () => {
      ringDeliveredRef.current = true;
      if (ringWatchdogRef.current) { clearTimeout(ringWatchdogRef.current); ringWatchdogRef.current = null; }
    });

    // In-call text line from the peer.
    s.on("call_chat", (d) => {
      if (!d?.text) return;
      setPeerTyping(false);
      setChatMessages((prev) => [...prev, { mine: false, text: String(d.text), from_name: d.from_name, msg_id: d.msg_id, ts: Date.now() }]);
    });

    // The peer read our messages — show a "Seen" tick.
    s.on("call_chat_seen", () => {
      setChatMessages((prev) => prev.map((m) => (m.mine ? { ...m, seen: true } : m)));
    });

    // Peer typing indicator (auto-clears if no update arrives).
    s.on("call_typing", (d) => {
      setPeerTyping(!!d?.typing);
      if (typingClearRef.current) clearTimeout(typingClearRef.current);
      if (d?.typing) typingClearRef.current = setTimeout(() => setPeerTyping(false), 4000);
    });

    // Pula Chat (DM) realtime — relayed to the Messages page via window events.
    s.on("dm_typing", (d) => window.dispatchEvent(new CustomEvent("pula:dm_typing", { detail: d })));
    s.on("dm_read", (d) => window.dispatchEvent(new CustomEvent("pula:dm_read", { detail: d })));

    // Mobile browsers drop the socket when the app is backgrounded / screen locked.
    // When the user returns, reconnect immediately so they show online again and can be called.
    const onVisible = () => {
      if (document.visibilityState === "visible" && s) {
        if (!s.connected) s.connect();
        else s.emit("presence_sync", {}, (res) => setOnlineIds(res?.online || []));
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
      s.disconnect(); socketRef.current = null; setConnected(false);
    };
  }, [user, cleanup, flushCandidates, ensureIce]);

  const checkPresence = useCallback((ids) => new Promise((resolve) => {
    if (!socketRef.current?.connected) return resolve([]);
    socketRef.current.emit("presence_check", { user_ids: ids }, (res) => resolve(res?.online || []));
  }), []);

  // Ask an offline member to call back (leaves them a notification).
  const requestCall = useCallback((target) => new Promise((resolve) => {
    if (!socketRef.current?.connected) { toast.error("Still connecting — try again in a moment."); return resolve(false); }
    socketRef.current.emit("call_request", { to: target.user_id }, (ack) => {
      if (ack?.ok) toast.success(`We'll let ${target.name || "them"} know you'd like to call.`);
      else toast.error("Couldn't send the call request.");
      resolve(!!ack?.ok);
    });
  }), []);

  // Query presence for a set of ids and merge the result into the live onlineIds.
  const refreshPresence = useCallback((ids) => {
    if (!socketRef.current?.connected || !ids?.length) return;
    socketRef.current.emit("presence_check", { user_ids: ids }, (res) => {
      const on = new Set(res?.online || []);
      setOnlineIds((prev) => {
        const set = new Set(prev);
        ids.forEach((id) => { if (on.has(id)) set.add(id); else set.delete(id); });
        return [...set];
      });
    });
  }, []);

  // Atomic "reachable right now?" check + graceful offline handling, done server-side.
  // Returns "online" | "offline" | "error". On offline it alerts the member (push + pending call)
  // so they can answer the instant they open Pula — a real ring, not just a "call back" note.
  const pingCall = useCallback((target, mode = "video") => new Promise((resolve) => {
    if (!socketRef.current?.connected) { toast.error("Still connecting — try again in a moment."); return resolve("error"); }
    socketRef.current.emit("call_ping", { to: target.user_id, media: mode }, (ack) => {
      if (ack?.online) return resolve("online");
      if (ack?.ok) {
        toast.success(`${target.name || "They"} isn't online right now — we've sent them a call alert. They'll see "\uD83D\uDCDE calling you" and can answer the moment they open Pula.`, {
          duration: 8000,
          action: target.user_id ? {
            label: "Remind them to turn on alerts",
            onClick: () => api.post("/calls/remind-alerts", { other_id: target.user_id })
              .then(({ data }) => toast.success(data?.already ? "They were already reminded today." : `We'll ask ${target.name || "them"} to turn on call alerts.`))
              .catch(() => toast.error("Couldn't send the reminder.")),
          } : undefined,
        });
        return resolve("offline");
      }
      toast.error("Couldn't reach them right now — please try again.");
      resolve("error");
    });
  }), []);

  // Check reachability first, then place a live call (or leave a ringing alert if they're away).
  const startCall = useCallback(async (target, mode = "video") => {
    const state = await pingCall(target, mode);
    if (state === "online") callUserRef.current?.(target, mode);
  }, [pingCall]);

  const callUser = useCallback(async (target, mediaMode = "video") => {
    if (!socketRef.current?.connected) { toast.error("Still connecting — try again in a moment."); return; }
    if (pcRef.current) { toast.error("You're already in a call."); return; }
    let mode = mediaMode;
    const eff = navigator.connection?.effectiveType;
    if (eff && /(^|-)2g/.test(eff) && mode === "video") {
      mode = "audio";
      toast.info("Weak signal detected — starting in voice-only to keep you connected.");
    }
    setMedia(mode);
    const tgt = { ...target, pic: target.pic || target.picture };
    setPeer(tgt);
    peerRef.current = tgt;
    setStatus("calling");
    roleRef.current = "caller";
    mediaRef.current = mode;
    callIdRef.current = `call_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    callStartRef.current = Date.now();
    reportedRef.current = false;
    ringDeliveredRef.current = false;
    everConnectedRef.current = false;
    restartCountRef.current = 0;
    try {
      await ensureIce();
      const pc = createPeer(target.user_id);
      await getLocal(pc, mode);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current.emit("call_offer", { to: target.user_id, offer, media: mode, call_id: callIdRef.current }, (ack) => {
        if (!ack?.ok) {
          if (ack?.error === "offline_notified") {
            toast.success(`${target.name} isn't online right now — we've sent them a call alert. They'll see "${'\uD83D\uDCDE'} calling you" and can answer as soon as they open Pula.`, { duration: 6000 });
          } else {
            toast.error(ack?.error === "unavailable" ? `${target.name} is offline right now.` : "Couldn't start the call.");
          }
          cleanup();
          return;
        }
        // They looked online — but if their device never confirms the ring within ~8s, their
        // socket was stale. Leave a wake-up alert and stop the pointless ringing.
        if (ringWatchdogRef.current) clearTimeout(ringWatchdogRef.current);
        ringWatchdogRef.current = setTimeout(() => {
          ringWatchdogRef.current = null;
          if (ringDeliveredRef.current || everConnectedRef.current || !pcRef.current) return;
          socketRef.current?.emit("call_offer_undelivered", { to: target.user_id, media: mode, call_id: callIdRef.current });
          toast.success(`${target.name} isn't reachable right now — we've sent them a call alert. They'll see "${'\uD83D\uDCDE'} calling you" and can answer as soon as they open Pula.`, { duration: 7000 });
          cleanup();
        }, 8000);
      });
    } catch {
      toast.error("Camera and microphone permission is needed to call.");
      cleanup();
    }
  }, [createPeer, getLocal, cleanup, ensureIce]);
  callUserRef.current = callUser;

  // One-tap "call back" from a missed-call notification: the notification opens the app with
  // ?callback=<uid>&cbn=<name>; once the socket is connected we auto-start the return call.
  const callbackDoneRef = useRef(false);
  useEffect(() => {
    if (!connected || callbackDoneRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("callback");
    if (!uid) return;
    callbackDoneRef.current = true;
    const name = params.get("cbn") || "";
    params.delete("callback"); params.delete("cbn");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    setTimeout(() => { try { startCall({ user_id: uid, name }, "video"); } catch { /* noop */ } }, 800);
  }, [connected, startCall]);

  const acceptCall = useCallback(async () => {
    const inc = incomingRef.current;
    if (!inc) return;
    const mode = inc.media || "video";
    setMedia(mode);
    roleRef.current = "callee";
    mediaRef.current = mode;
    callStartRef.current = Date.now();
    reportedRef.current = false;
    try {
      await ensureIce();
      const pc = createPeer(inc.from);
      await getLocal(pc, mode);
      await pc.setRemoteDescription(new RTCSessionDescription(inc.offer));
      remoteSet.current = true;
      await flushCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socketRef.current?.emit("call_answer", { to: inc.from, answer, call_id: callIdRef.current });
      incomingRef.current = null;
      setIncoming(null);
      // Don't claim "Connected" yet — wait for real media (onconnectionstatechange).
      // Otherwise the callee sees "Connected" over a black/silent screen when media fails.
      setStatus("connecting");
    } catch {
      toast.error("Camera and microphone permission is needed.");
      if (inc) socketRef.current?.emit("call_reject", { to: inc.from });
      cleanup();
    }
  }, [createPeer, getLocal, flushCandidates, cleanup, ensureIce]);

  const rejectCall = useCallback(() => {
    const inc = incomingRef.current;
    if (inc) socketRef.current?.emit("call_reject", { to: inc.from });
    cleanup();
  }, [cleanup]);

  const endCall = useCallback(() => {
    const to = peerRef.current?.user_id;
    if (to) socketRef.current?.emit("call_end", { to });
    reportCall("ended");
    cleanup();
  }, [cleanup, reportCall]);
  endCallRef.current = endCall;

  const sendChat = useCallback((text) => {
    const t = String(text || "").trim();
    if (!t || !peerRef.current) return;
    socketRef.current?.emit("call_chat", { to: peerRef.current.user_id, text: t, call_id: callIdRef.current });
    setChatMessages((prev) => [...prev, { mine: true, text: t, ts: Date.now(), seen: false }]);
  }, []);

  const markChatSeen = useCallback(() => {
    if (!peerRef.current) return;
    socketRef.current?.emit("call_chat_seen", { to: peerRef.current.user_id, call_id: callIdRef.current });
  }, []);

  const sendTyping = useCallback((typing) => {
    if (!peerRef.current) return;
    socketRef.current?.emit("call_typing", { to: peerRef.current.user_id, typing: !!typing });
  }, []);

  const sendDmTyping = useCallback((to, conversationId, on) => {
    socketRef.current?.emit("dm_typing", { to, conversation_id: conversationId, on: !!on });
  }, []);

  const toggleMute = useCallback(() => {
    const t = localStreamRef.current?.getAudioTracks?.()[0];
    if (t) { t.enabled = !t.enabled; setMuted(!t.enabled); }
  }, []);

  const toggleCamera = useCallback(() => {
    const t = localStreamRef.current?.getVideoTracks?.()[0];
    if (t) { t.enabled = !t.enabled; setCameraOff(!t.enabled); }
  }, []);

  const shareScreen = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!sender) { toast.info("Screen sharing is available on video calls."); return; }
    try {
      const ds = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = ds.getVideoTracks()[0];
      await sender.replaceTrack(screenTrack);
      setSharing(true);
      screenTrack.onended = async () => {
        const cam = localStreamRef.current?.getVideoTracks?.()[0];
        if (cam) await sender.replaceTrack(cam);
        setSharing(false);
      };
    } catch { /* user cancelled */ }
  }, []);

  const setBackground = useCallback(async (key) => {
    if (media !== "video" || !pcRef.current || !localStreamRef.current) return;
    const sender = pcRef.current.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!sender) return;
    const conf = BACKGROUNDS.find((b) => b.key === key);
    if (!conf) return;
    if (key === "none") {
      const cam = localStreamRef.current.getVideoTracks()[0];
      if (cam) await sender.replaceTrack(cam);
      try { vbgRef.current?.stop(); } catch { /* ignore */ }
      vbgRef.current = null;
      setBgMode("none");
      return;
    }
    try {
      if (!vbgRef.current) {
        const { VirtualBackground } = await import("@/lib/virtualBackground");
        const vbg = new VirtualBackground(localStreamRef.current);
        const out = await vbg.start(key, conf.url);
        vbgRef.current = vbg;
        const t = out.getVideoTracks()[0];
        if (t) await sender.replaceTrack(t);
      } else {
        vbgRef.current.setBackground(key, conf.url);
      }
      setBgMode(key);
    } catch {
      toast.error("Backgrounds need a stronger device/connection.");
      setBgMode("none");
    }
  }, [media]);

  const answerWakeup = useCallback(() => {
    const w = wakeup;
    setWakeup(null);
    if (w) callUser({ user_id: w.user_id, name: w.name }, w.media || "video");
  }, [wakeup, callUser]);
  const dismissWakeup = useCallback(() => setWakeup(null), []);

  const value = {
    connected, status, peer, incoming, localStream, remoteStream, callDiag, reconnecting, callQuality, chatMessages, peerTyping,
    muted, cameraOff, media, sharing, onlineIds, recording, bgMode, wakeup,
    callUser, acceptCall, rejectCall, endCall, toggleMute, toggleCamera, shareScreen, toggleRecording, setBackground, checkPresence, refreshPresence, requestCall, pingCall, startCall, answerWakeup, dismissWakeup, sendChat, sendTyping, sendDmTyping, markChatSeen,
    // group calling
    groupState, groupIncoming, groupParticipants, groupStreams, groupLocalStream, groupMuted, groupCameraOff, groupTitle, groupChatMessages, groupTypingName,
    startGroupCall, inviteToGroup, acceptGroup, declineGroup, leaveGroup, toggleGroupMute, toggleGroupCamera, sendGroupChat, sendGroupTyping,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
